import { useEffect, useRef, useState, type FormEvent } from "react";

/**
 * Modal that asks for a 6-digit TOTP code. Promise-based so callers
 * can `await openTotpPrompt()` from any flow without rebuilding their
 * own state machine. Returns `null` if the user cancels.
 *
 * Usage:
 *   const code = await openTotpPrompt({ title: "Confirm purchase" });
 *   if (code === null) return;
 *   await pay({ custom_id, totp_code: code });
 */

interface PromptOptions {
  title?: string;
  description?: string;
  /** Pre-fill the field - useful for re-prompting after a wrong code. */
  initial?: string;
  /** Show a custom error message above the input. */
  errorMessage?: string;
}

interface PromptState {
  options: PromptOptions;
  resolve: (code: string | null) => void;
}

let _open: ((options: PromptOptions) => Promise<string | null>) | null = null;

/** Programmatic entry point - usable from any component / hook. */
export function openTotpPrompt(
  options: PromptOptions = {},
): Promise<string | null> {
  if (_open === null) {
    return Promise.reject(
      new Error("<TotpPromptHost /> is not mounted; cannot show 2FA prompt"),
    );
  }
  return _open(options);
}

/**
 * Mounted once, near the toaster. Listens for `openTotpPrompt` calls
 * and renders a modal that resolves the caller's promise on submit /
 * cancel.
 */
export function TotpPromptHost() {
  const [state, setState] = useState<PromptState | null>(null);

  useEffect(() => {
    _open = (options) =>
      new Promise<string | null>((resolve) => {
        setState({ options, resolve });
      });
    return () => {
      _open = null;
    };
  }, []);

  if (state === null) return null;

  const close = (value: string | null) => {
    state.resolve(value);
    setState(null);
  };

  return <TotpPromptModal state={state} onClose={close} />;
}

function TotpPromptModal({
  state,
  onClose,
}: {
  state: PromptState;
  onClose: (value: string | null) => void;
}) {
  const [code, setCode] = useState(state.options.initial ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose(null);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = code.replace(/\s+/g, "");
    if (!/^\d{6}$/.test(trimmed)) return;
    onClose(trimmed);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4"
      onClick={() => onClose(null)}
    >
      <div
        className="card w-full max-w-sm overflow-hidden bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={submit} className="space-y-4 p-5">
          <div>
            <h2 className="text-base font-semibold text-slate-800">
              {state.options.title ?? "Two-factor code"}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              {state.options.description ??
                "Enter the 6-digit code from your authenticator app."}
            </p>
          </div>

          {state.options.errorMessage && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {state.options.errorMessage}
            </div>
          )}

          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            autoComplete="one-time-code"
            placeholder="123 456"
            className="input text-center text-lg tracking-[0.4em]"
            value={code}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
          />

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => onClose(null)}
              className="btn btn-ghost text-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary text-xs"
              disabled={!/^\d{6}$/.test(code)}
            >
              Confirm
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
